import dayjs from "dayjs";
import {
  type FC,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  LOCAL_STORAGE_FAVORITE_EMAILS_KEY,
  LOCAL_STORAGE_INVISIBLE_EMAILS_KEY,
  LOCAL_STORAGE_READ_EMAILS_KEY,
} from "../../../constants/local-storage-keys";
import {
  DEFAULT_CONTEXT_MENU_STATUS,
  DEFAULT_SEARCH_FILTER,
  SEARCH_LIMIT,
  SEARCH_OFFSET,
} from "../../../constants/search-fiilter";
import {
  type EmailDocument,
  type EmailDocuments,
  type EmailSearchInput,
  RecruitmentType,
  useEmailsRestApi,
} from "../../../hooks/useEmailsRestApi";
import { ContextMenuUI } from "./components/ContextMenu";
import { EmailDetailModal } from "./components/EmailDetailModal";
import type { ChangeIsMatterHandler } from "./components/SearchEmailsForm";
import LocalStorageEmails, { type ReadEmailRecord } from "./localStorageEmails";
import SearchEmailsPage from "./SearchEmailsPage";
import { SearchConditionsStorage } from "./searchConditionsStorage";

export type SelectContextMenuHandler = (status: ContextMenuStatus) => void;

export type ContextMenuStatus = {
  id: string;
  visible: boolean;
  clientX: number;
  clientY: number;
};

const INITIAL_SELECTED_EMAIL: EmailWithStatus = {
  id: "",
  recruitmentType: RecruitmentType.Matter,
  subject: "",
  content: "",
  date: new Date().toISOString(),
  from: {
    address: "",
    name: "",
  },
  favorite: false,
  read: false,
};

export type EmailsWithStatus = {
  nodes: EmailWithStatus[];
} & Omit<EmailDocuments, "nodes">;

export type EmailWithStatus = {
  favorite: boolean;
  read: boolean;
} & EmailDocument;

// メールの既読ステータスを保存する日数
const RETENTION_DAYS = 5;

const localStorageReadEmails = new LocalStorageEmails(
  LOCAL_STORAGE_READ_EMAILS_KEY,
);

const SearchEmailsPageContainer: FC = () => {
  const navigate = useNavigate();

  // 初期化時に保存された検索条件を復元
  const [currentSearchInput, setCurrentSearchInput] =
    useState<EmailSearchInput>(() => {
      const saved = SearchConditionsStorage.loadSearchConditions();
      return saved.conditions || DEFAULT_SEARCH_FILTER;
    });

  const localStorageInvisibledEmailIds = useMemo(
    (): LocalStorageEmails =>
      new LocalStorageEmails(LOCAL_STORAGE_INVISIBLE_EMAILS_KEY),
    [],
  );

  const localStorageFavoriteEmailIds = useMemo(
    (): LocalStorageEmails =>
      new LocalStorageEmails(LOCAL_STORAGE_FAVORITE_EMAILS_KEY),
    [],
  );

  const [contextmenuStatus, setContextMenuStatus] = useState<ContextMenuStatus>(
    DEFAULT_CONTEXT_MENU_STATUS,
  );

  const [invisibledEmailIds, setInvisibleEmailIds] = useState<string[]>(
    localStorageInvisibledEmailIds.getEmailIds(),
  );
  const [favoEmailIds, setFavoEmailIds] = useState<string[]>(
    localStorageFavoriteEmailIds.getEmailIds(),
  );

  const [isLoading, setIsLoading] = useState(true);
  const [searchLimit, setSearchLimit] = useState(SEARCH_LIMIT);
  // REST API を使用してメールを取得
  const { error, data, refetch } = useEmailsRestApi({
    variables: {
      filter: currentSearchInput,
      offset: 0,
      limit: SEARCH_LIMIT,
    },
    onCompleted: () => {
      setIsLoading(false);
    },
    notifyOnNetworkStatusChange: true,
  });
  const [selectedEmail, setSelectedEmail] = useState<EmailWithStatus>(
    INITIAL_SELECTED_EMAIL,
  );

  // 案件メールを保持する配列
  const [matterEmails, setMatterEmails] = useState<EmailsWithStatus>({
    totalCount: 0,
    nodes: [],
  });
  // 人材メールを保持する配列
  const [hrEmails, setHREmails] = useState<EmailsWithStatus>({
    totalCount: 0,
    nodes: [],
  });

  const [modalVisibility, setModalVisibility] = useState(false);

  // 初期化時に保存されたisMatterも復元
  const [isMatter, setIsMatter] = useState(() => {
    const saved = SearchConditionsStorage.loadSearchConditions();
    return saved.isMatter !== null ? saved.isMatter : true;
  });

  const [readEmailRecords, setReadEmailRecords] = useState<ReadEmailRecord[]>(
    () => {
      const cutoffTime = dayjs().subtract(RETENTION_DAYS, "day").valueOf();
      return localStorageReadEmails.filterExpiredEmailIdRecords(cutoffTime);
    },
  );

  // Setを使用して既読メールIDを高速に検索
  const readEmailIdsSet = useMemo(() => {
    return new Set(readEmailRecords.map(record => record.id));
  }, [readEmailRecords]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted && data) {
      const filteredEmails = {
        totalCount: data.emails.totalCount,
        nodes: data.emails.nodes
          .map(
            (email: EmailDocument): EmailWithStatus => ({
              ...email,
              favorite: favoEmailIds.includes(email.id),
              read: readEmailIdsSet.has(email.id),
            }),
          )
          .filter(
            (email: EmailWithStatus) => !invisibledEmailIds.includes(email.id),
          ),
      };
      if (isMatter) {
        setMatterEmails(filteredEmails);
      } else {
        setHREmails(filteredEmails);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [data, invisibledEmailIds, favoEmailIds, isMatter, readEmailIdsSet]);

  // 初回ロード時は上記useEmailsRestApiでlocalStorageから復元した条件で実行される

  const markAsRead = useCallback(
    (id: string) => {
      if (!readEmailIdsSet.has(id)) {
        localStorageReadEmails.appendEmailIdWithTimestamp(id);
        const newRecords = localStorageReadEmails.getEmailIdRecords();
        setReadEmailRecords(newRecords);
      }
    },
    [readEmailIdsSet],
  );

  const onSelectEmail = useCallback(
    (id: string) => {
      markAsRead(id);

      // freeWordsをクエリパラメータに追加
      const freeWordsParam =
        currentSearchInput.freeWords && currentSearchInput.freeWords.length > 0
          ? `?freeWords=${currentSearchInput.freeWords.join(",")}`
          : "";

      navigate(`/emails/search/${id}${freeWordsParam}`);
    },
    [markAsRead, currentSearchInput.freeWords, navigate],
  );

  const onSelectContextMenu: SelectContextMenuHandler = useCallback(
    (status: ContextMenuStatus) => {
      setContextMenuStatus(status);
    },
    [],
  );
  const generateModifiedSearchInput = useCallback(
    (searchInput: EmailSearchInput): EmailSearchInput => {
      const copy = JSON.parse(JSON.stringify(searchInput));
      // タイプを切り替えるときはhrFilterまたはmatterFilterのオブジェクト自体を削除する
      if (copy.recruitmentType === RecruitmentType.Matter) {
        delete copy.hrFilter;
      } else if (copy.recruitmentType === RecruitmentType.HumanResource) {
        delete copy.matterFilter;
      }
      // NOTE: freeWords配列に空文字が入っていると正しくレスポンスを返さないことがある
      if (copy.freeWords) {
        copy.freeWords = copy.freeWords.filter((v: string) => v);
        if (copy.freeWords.length === 0) {
          delete copy.freeWords;
        }
      }
      return copy;
    },
    [],
  );
  const onSearch: SubmitHandler<EmailSearchInput> = useCallback(
    async (data: EmailSearchInput) => {
      try {
        setIsLoading(true);
        const modifiedInput = generateModifiedSearchInput(data);
        setCurrentSearchInput(modifiedInput); // 先にstateを更新

        // 検索条件をlocalStorageに保存
        SearchConditionsStorage.saveSearchConditions(modifiedInput, isMatter);

        await refetch({
          filter: modifiedInput,
          offset: 0,
          limit: SEARCH_LIMIT,
        });
        setSearchLimit(SEARCH_LIMIT);
        if (selectedEmail.subject !== "") {
          setSelectedEmail(INITIAL_SELECTED_EMAIL);
        }
      } catch (_error) {
        // エラーを無視
      } finally {
        setIsLoading(false);
      }
    },
    [selectedEmail, refetch, generateModifiedSearchInput, isMatter],
  );

  const resetFilter = useCallback((recruitmentType: RecruitmentType) => {
    setCurrentSearchInput({
      ...DEFAULT_SEARCH_FILTER,
      recruitmentType: recruitmentType,
      freeWords: [],
    });
    setSearchLimit(SEARCH_LIMIT);
  }, []);

  const onClearFilter = useCallback(async () => {
    resetFilter(currentSearchInput.recruitmentType!);
    localStorageInvisibledEmailIds.resetEmailIds();
    localStorageFavoriteEmailIds.resetEmailIds();
    setInvisibleEmailIds([]);
    setFavoEmailIds([]);
  }, [
    currentSearchInput,
    resetFilter,
    localStorageInvisibledEmailIds,
    localStorageFavoriteEmailIds,
  ]);

  const onClosedModal: MouseEventHandler = useCallback(async event => {
    event.preventDefault();
    setModalVisibility(false);
  }, []);

  const onClosedMenu: MouseEventHandler = useCallback(async event => {
    event.preventDefault();
    setContextMenuStatus(DEFAULT_CONTEXT_MENU_STATUS);
  }, []);

  const addInvisibleEmailId = useCallback(
    (emailId: string) => {
      localStorageInvisibledEmailIds.appendEmailId(emailId);
      setInvisibleEmailIds(localStorageInvisibledEmailIds.getEmailIds());
    },
    [localStorageInvisibledEmailIds],
  );

  const onToggleFavoriteEmail = useCallback(
    (emailId: string, favorite: boolean) => {
      favorite
        ? localStorageFavoriteEmailIds.appendEmailId(emailId)
        : localStorageFavoriteEmailIds.removeEmailId(emailId);
      setFavoEmailIds(localStorageFavoriteEmailIds.getEmailIds());
    },
    [localStorageFavoriteEmailIds],
  );

  const onSelectedMenu: MouseEventHandler = useCallback(
    async event => {
      event.preventDefault();
      addInvisibleEmailId(contextmenuStatus.id);
      setContextMenuStatus(DEFAULT_CONTEXT_MENU_STATUS);
    },
    [contextmenuStatus, addInvisibleEmailId],
  );

  const onChangeIsMatter: ChangeIsMatterHandler = useCallback(
    async (isMatter: boolean) => {
      setIsMatter(isMatter);
      const newRecruitmentType = isMatter
        ? RecruitmentType.Matter
        : RecruitmentType.HumanResource;
      const newFilter = {
        ...DEFAULT_SEARCH_FILTER,
        recruitmentType: newRecruitmentType,
        freeWords: [],
      };
      setCurrentSearchInput(newFilter);
      setSearchLimit(SEARCH_LIMIT);
      // isMatter変更時も検索条件を保存
      SearchConditionsStorage.saveSearchConditions(newFilter, isMatter);

      // ここでメールデータを再取得
      await refetch({
        filter: newFilter,
        offset: 0,
        limit: SEARCH_LIMIT,
      });
    },
    [refetch],
  );

  const onShowMore: MouseEventHandler = useCallback(
    async event => {
      event.preventDefault();
      setIsLoading(true);
      const newLimit = searchLimit + SEARCH_OFFSET;
      setSearchLimit(newLimit);

      await refetch({
        filter: generateModifiedSearchInput(currentSearchInput),
        offset: 0,
        limit: newLimit,
      });
    },
    [currentSearchInput, searchLimit, refetch, generateModifiedSearchInput],
  );

  if (error) {
    return <div>{JSON.stringify(error, null, 2)}</div>;
  }

  return (
    <>
      <SearchEmailsPage
        currentSearchInput={currentSearchInput}
        isMatter={isMatter}
        isLoading={isLoading}
        selectedEmail={selectedEmail}
        invisibleEmailNums={invisibledEmailIds.length}
        onToggleFavoriteEmail={onToggleFavoriteEmail}
        matterEmails={matterEmails}
        hrEmails={hrEmails}
        onShowMore={onShowMore}
        onSelectEmail={onSelectEmail}
        onSelectContextMenu={onSelectContextMenu}
        onSearch={onSearch}
        onClearFilter={onClearFilter}
        onChangeIsMatter={onChangeIsMatter}
      />

      <EmailDetailModal
        visibility={modalVisibility}
        selectedEmail={selectedEmail}
        onClosed={onClosedModal}
        freeWords={currentSearchInput.freeWords || []}
      />

      <ContextMenuUI
        status={contextmenuStatus}
        onClosed={onClosedMenu}
        onSelectedMenu={onSelectedMenu}
      />
    </>
  );
};

export default SearchEmailsPageContainer;
